import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SITE } from '../data/site';
import Seo from '../components/Seo';
import JsonLd from '../components/JsonLd';
import { aggregateRatingSchema, faqSchema, reviewSchema } from '../lib/schema';
import { useGoogleReviews } from '../lib/reviews';
import { useBodyClass } from '../lib/body-class';
import { CtaPanel, PhotoHero, SectionHeading, StarRating } from '../components/ui';
import HomeFAQ from '../components/HomeFAQ';
import { HOME_FAQS } from '../components/home/homeFaqs';
import HomeIntro from '../components/home/HomeIntro';
import AnnouncementBar from '../components/home/AnnouncementBar';
import PillarCards from '../components/home/PillarCards';
import TeamStrip from '../components/home/TeamStrip';
import Stories from '../components/home/Stories';
import Testimonials from '../components/home/Testimonials';
import HomeStatement from '../components/home/HomeStatement';
import ConditionsTeaser from '../components/home/ConditionsTeaser';
import GuidesRow from '../components/home/GuidesRow';
import { HERO_IMAGE } from '../components/home/homeImages';
import ProofLine from '../components/home/ProofLine';

const homepageFaqSchema = HOME_FAQS.map((faq) => ({
  question: faq.q,
  answer: faq.a,
}));

export default function Home() {
  useBodyClass(['home', 'page', 'layout-one-col']);
  const reviewsState = useGoogleReviews();

  return (
    <>
      <Seo
        title="Chiropractor Royal Oak MI | Van Every Chiropractic" exactTitle
        description="Chiropractor Royal Oak focuses on rejuvenating your family's health. We offer Saturday appointments and accept insurance. Call or email us today!"
        canonical="/"
      />
      {/* Keep review JSON-LD disabled until Zach reviews and flips
          SITE.hasVerifiedGoogleReviews. Sourced from the live /api/reviews
          data (never the removed synthetic testimonials) so it is accurate
          the moment it is turned on. */}
      {SITE.hasVerifiedGoogleReviews && reviewsState.status === 'ready' && (
        <>
          <JsonLd
            data={aggregateRatingSchema({
              ratingValue: reviewsState.data.rating ?? 0,
              reviewCount: reviewsState.data.reviewCount,
            })}
          />
          {reviewsState.data.reviews.map((review, i) => (
            <JsonLd
              key={i}
              data={reviewSchema({
                author: review.author,
                datePublished: review.publishTime ?? reviewsState.data.fetchedAt,
                reviewBody: review.text,
                ratingValue: review.rating,
              })}
            />
          ))}
        </>
      )}
      <JsonLd data={faqSchema(homepageFaqSchema)} />

      <main id="main" tabIndex={-1}>
        <PhotoHero
          size="home"
          align="left"
          className="ve-photo-hero--side"
          titleId="home-title"
          title="A Royal Oak Chiropractor for the Whole Family"
          subtitle="Gentle, nervous-system focused care for babies, kids, moms-to-be, and the whole family."
          image={HERO_IMAGE}
          primary={{ label: 'Schedule Your First Visit', to: SITE.booking.chooserPath }}
          secondary={{ label: 'Meet the Doctors', to: '/meet-the-doctors/' }}
          scrollTargetId="home-intro"
        >
          <ProofLine />
        </PhotoHero>

        <div id="home-intro" className="ve-home-body">
          <HomeIntro />

          <AnnouncementBar
            headingId="home-announce-title"
            title="Free parent webinar: The Perfect Storm"
            text="Focus struggles, big emotions, restless nights, tummy troubles. Our doctors explain the nervous system connection. Free, about an hour, watch anytime."
            button={{ label: 'See the webinar', to: '/the-perfect-storm/' }}
          />

          <section className="ve-section ve-home-pillars-section" aria-labelledby="home-pillars-title">
            <div className="ve-container">
              <SectionHeading
                eyebrow="How we help"
                title="Gentle care for every age and stage"
                id="home-pillars-title"
                align="center"
              />
              <PillarCards />
            </div>
          </section>

          <HomeStatement />

          <CtaPanel
            text="Ready for a gentle first visit? Book online in a couple of minutes, or call (248) 616-0900 and our front desk will find a time that fits your family."
            button={{ label: 'Schedule Your First Visit', to: SITE.booking.chooserPath }}
          />

          <TeamStrip />

          <Testimonials />

          <Stories />
        </div>
      </main>
    </>
  );
}

export function HomeAfterWrap() {
  const reviewsState = useGoogleReviews();
  // This chunk hydrates late; if /api/reviews has already answered by then,
  // the hook starts 'ready' while the prerendered HTML has no proof line
  // (React #418). Show the live line only after mount so hydration matches.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const proof =
    mounted && reviewsState.status === 'ready' && reviewsState.data.rating ? (
      <>
        <StarRating rating={reviewsState.data.rating} label={`${reviewsState.data.rating.toFixed(1)} out of 5 stars on Google`} />
        <span>
          {reviewsState.data.rating.toFixed(1)} on Google from {reviewsState.data.reviewCount} reviews
        </span>
      </>
    ) : undefined;

  return (
    <div className="ve-home-after">
      <ConditionsTeaser />
      <GuidesRow />
      <HomeFAQ />
      {/* This chunk renders after <main>; a region named by the CTA heading keeps it in a landmark. */}
      <section aria-labelledby="home-cta-title">
        <CtaPanel
          variant="large"
          headingId="home-cta-title"
          title="A Gentle Touch for Your Comfort"
          text={(
            <>
              Your first visit starts with painless testing, so we can see how stress has affected
              your body. Then our doctors use light, nervous-system focused contacts with no cracking,
              twisting, or popping. Learn more about our{' '}
              <Link to="/gentle-chiropractor-no-cracking/">gentle, no-cracking approach</Link>. We see
              patients on Saturdays, accept insurance, and offer massage therapy on site.
            </>
          )}
          button={{ label: 'Schedule Your First Visit', to: SITE.booking.chooserPath }}
          proof={proof}
        />
      </section>
    </div>
  );
}
